import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Camera } from "lucide-react";
import { WallShareCard } from "@/components/WallShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialsOf = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts.slice(0, 2).map((p) => p[0].toUpperCase()).join(".") + "." : "—";
};

export const WallPost = ({ fullName = "", initialDisplay = null, photo = null, initialShowPhoto = false }) => {
  const [display, setDisplay] = useState(initialDisplay || "anonymous");
  const [showPhoto, setShowPhoto] = useState(Boolean(initialShowPhoto));
  const [posted, setPosted] = useState(initialDisplay);
  const [postedShowPhoto, setPostedShowPhoto] = useState(Boolean(initialShowPhoto));
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (initialDisplay) { setDisplay(initialDisplay); setPosted(initialDisplay); } }, [initialDisplay]);
  useEffect(() => { setShowPhoto(Boolean(initialShowPhoto)); setPostedShowPhoto(Boolean(initialShowPhoto)); }, [initialShowPhoto]);

  const first = fullName.trim().split(/\s+/)[0] || "you";
  const options = [
    { id: "anonymous", label: "Anonymous", hint: "Just the card" },
    { id: "initials", label: "Initials", hint: initialsOf(fullName) },
    { id: "first_name", label: "First name", hint: first },
  ];

  const canPhoto = Boolean(photo) && display !== "anonymous";
  const effectiveShowPhoto = showPhoto && canPhoto;
  const dirty = display !== posted || effectiveShowPhoto !== postedShowPhoto;

  const post = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/graduates/wall/post`, { display, show_photo: effectiveShowPhoto });
      setPosted(r.data.wall_display);
      setPostedShowPhoto(Boolean(r.data.wall_show_photo));
      toast.success(
        r.data.wall_name
          ? `You're on the wall as ${r.data.wall_name}${r.data.wall_show_photo ? " · with your photo" : ""}.`
          : "You're on the wall — anonymously.",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post to the wall.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 pt-8 border-t border-[#E7DFD5]" data-testid="wall-post">
      <div className="flex items-center gap-2 mb-2">
        <Users size={14} className="text-[#B45309]" />
        <div className="section-tag">§ The Graduate Wall</div>
      </div>
      <p className="text-[#78716C] text-sm max-w-xl mb-4">
        Your card sits on the public wall at tacc.love — anonymous by default. Choose how it reads.
      </p>
      <div className="flex flex-wrap gap-2 mb-4" role="radiogroup" aria-label="How your wall card reads">
        {options.map((o) => (
          <button
            key={o.id}
            role="radio"
            aria-checked={display === o.id}
            onClick={() => setDisplay(o.id)}
            data-testid={`wall-post-option-${o.id}`}
            className={`px-4 py-2.5 rounded-full border text-left transition-colors duration-300 ${display === o.id ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#1C1917]" : "border-[#E7DFD5] text-[#44403C] hover:border-[#E7DFD5]"}`}
          >
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase block">{o.label}</span>
            <span className="font-display text-base text-[#B45309]">{o.hint}</span>
          </button>
        ))}
      </div>
      {photo && (
        <label
          data-testid="wall-post-photo-toggle"
          className={`inline-flex items-center gap-3 mb-5 select-none ${canPhoto ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
        >
          <input
            type="checkbox"
            checked={effectiveShowPhoto}
            disabled={!canPhoto}
            onChange={(e) => setShowPhoto(e.target.checked)}
            data-testid="wall-post-photo-checkbox"
            className="accent-[#F59E0B] w-4 h-4"
          />
          <img src={photo} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover border border-[#F59E0B]/40" />
          <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#44403C] inline-flex items-center gap-1.5">
            <Camera size={12} /> Show my photo{canPhoto ? "" : " · pick initials or first name first"}
          </span>
        </label>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <button onClick={post} disabled={busy || (!!posted && !dirty)} data-testid="wall-post-btn" className="btn-ember text-xs disabled:opacity-50">
          {posted ? (dirty ? "Update the wall" : "On the wall") : "Post to the wall"}
        </button>
        {posted && (
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-300" data-testid="wall-post-status">
            Live · {posted === "anonymous" ? "anonymous" : posted === "initials" ? initialsOf(fullName) : first}{postedShowPhoto ? " · photo" : ""}
          </span>
        )}
        <WallShareCard wallName={(posted || "anonymous") === "anonymous" ? null : posted === "initials" ? initialsOf(fullName) : first} />
      </div>
    </div>
  );
};
